import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { gaps } from "../../api/gaps";
import { useSession } from "../../auth/AuthProvider";
import { Banner, ButtonLink, Card, ErrorState, MoneyStat, PageHeader, SkeletonBlock, StatusPill } from "../../components/ui";

// Advertiser home. UX-DESIGN.md §2.1 (compliance banner states) still
// needs G-10; §2.2's spend status comes from GET /v1/wallet (G-23, 0056).
// The spend-freeze banner itself is on every advertiser screen (AppShell).

export function AdvertiserOverview() {
  const session = useSession();
  const ep = useEndpoints();
  const compliance = useQuery({ queryKey: ["compliance"], queryFn: gaps.getComplianceStatus, retry: false });
  const wallet = useQuery({ queryKey: ["wallet"], queryFn: () => ep.advertiser.getWallet() });

  return (
    <div className="stack-lg">
      <PageHeader
        title="Overview"
        description="Build campaigns, fund your wallet, and track spend."
        actions={<ButtonLink to="/advertiser/campaigns/new" variant="primary">New campaign</ButtonLink>}
      />
      {session.mfa === "not_enrolled" ? (
        <Banner tone="warning" title="Set up two-step verification">
          Your wallet is locked until you do. <Link to="/advertiser/security?setup=1">Set it up now</Link>.
        </Banner>
      ) : null}

      <div className="grid-2">
        <Card title="Account verification" subtitle="Campaigns can be drafted and reviewed while verification runs; they can't go live until it clears.">
          {compliance.isError ? <ErrorState error={compliance.error} /> : null}
        </Card>
        <Card title="Spend status" subtitle="Whether new reservations are currently allowed on this account.">
          {wallet.isPending ? (
            <SkeletonBlock lines={2} label="Loading spend status" />
          ) : wallet.isError ? (
            <ErrorState error={wallet.error} onRetry={() => void wallet.refetch()} />
          ) : (
            <div className="stack-sm">
              {wallet.data.spend_frozen ? (
                <StatusPill tone="error">Spending frozen — new reservations blocked</StatusPill>
              ) : (
                <StatusPill tone="success">Spending allowed</StatusPill>
              )}
              {wallet.data.by_currency.map((r) => (
                <MoneyStat key={r.currency} label="Spendable" value={r.spendable} currency={r.currency} note="Available for new reservations" />
              ))}
              <Link to="/advertiser/wallet">Wallet details</Link>
            </div>
          )}
        </Card>
      </div>

      <Card title="Get started">
        <ol className="stack-sm" style={{ margin: 0, paddingLeft: "1.25rem" }}>
          <li>
            <Link to="/advertiser/security">Turn on two-step verification</Link> — required for the wallet.
          </li>
          <li>
            <Link to="/advertiser/wallet">Add funds</Link> through our payment provider's checkout.
          </li>
          <li>
            <Link to="/advertiser/campaigns/new">Build a campaign</Link> — schedule, targeting, bid and budget, creative, then review and
            submit.
          </li>
          <li>Verify your destination domain and wait for review; then promote the approved release.</li>
        </ol>
      </Card>
    </div>
  );
}
